import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import * as PATHS from 'new/constants/pathnames';
import { useSnackbar } from 'new/contexts/SnackbarContext';
import { useConfirmDialog } from 'new/elements/ConfirmDialog/useConfirmDialog';
import { CAPSULES_TABLE_PAGE_SIZE } from 'new/modules/settings/capsules/constants/capsulesTable';
import { useCapsulesSettings } from 'new/modules/settings/capsules/hooks/useCapsulesSettings';
import type { RegisterCapsuleDeployPayload } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal';
import { useCapsulesTablePagination } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/hooks/useCapsulesTablePagination';
import { getCreateClusterErrorMessage } from 'new/modules/settings/capsules/utils/getCreateClusterErrorMessage';
import { getMutationErrorMessage } from 'new/modules/settings/capsules/utils/getMutationErrorMessage';
import type {
  CapsuleRowMenuAction,
  CapsuleTableRow,
} from 'new/modules/settings/capsules/viewModels/capsulesTableViewModel';
import type { CapsuleDto } from 'new/stores/capsules/capsules.endpoints';

export function useCapsulesSettingsScreen() {
  const history = useHistory();
  const [isRegisterCapsuleModalOpen, setRegisterCapsuleModalOpen] = useState(false);

  const {
    rows,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
    canRegisterCapsule,
    registerCapsule,
    isCreating,
    deleteCapsule,
  } = useCapsulesSettings();

  const { showSuccess, showError: showSnackbarError } = useSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const handleRowClick = useCallback(
    (row: CapsuleTableRow) => {
      history.push(`${ROUTE_REDESIGN}${PATHS.buildSettingsCapsuleDetailPath(row.id)}`);
    },
    [history],
  );

  const handleRegisterCapsule = useCallback(() => {
    if (!canRegisterCapsule) return;
    setRegisterCapsuleModalOpen(true);
  }, [canRegisterCapsule]);

  const handleCloseRegisterCapsuleModal = useCallback(() => {
    setRegisterCapsuleModalOpen(false);
  }, []);

  const handleContinueToDeploy = useCallback(
    (createdClusterId: string | number | null) => {
      setRegisterCapsuleModalOpen(false);
      if (createdClusterId == null) return;

      history.push(
        `${ROUTE_REDESIGN}${PATHS.buildSettingsCapsuleDetailPath(String(createdClusterId))}`,
      );
    },
    [history],
  );

  const handleRegisterKeys = useCallback(
    async (deployPayload: RegisterCapsuleDeployPayload): Promise<CapsuleDto | undefined> => {
      try {
        return await registerCapsule(deployPayload);
      } catch (err: unknown) {
        const userFacingMessage = getCreateClusterErrorMessage(err);
        showSnackbarError(userFacingMessage);
        return undefined;
      }
    },
    [registerCapsule, showSnackbarError],
  );

  const handleRowMenuAction = useCallback(
    (action: CapsuleRowMenuAction, row: CapsuleTableRow) => {
      if (!canRegisterCapsule) return;

      switch (action) {
        case 'viewDetails':
          handleRowClick(row);
          break;
        case 'delete':
          confirm({
            title: 'Delete capsule',
            message: `Delete "${row.name}"? This cannot be undone.`,
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: async () => {
              try {
                await deleteCapsule(row.id).unwrap();
                showSuccess('Capsule deleted');
              } catch (mutationError) {
                const message = getMutationErrorMessage(mutationError, 'Failed to delete capsule');
                showSnackbarError(message);
              }
            },
          });
          break;
        default:
          break;
      }
    },
    [canRegisterCapsule, confirm, deleteCapsule, handleRowClick, showSnackbarError, showSuccess],
  );

  const { paginatedRows, page, pageCount, onPageChange, showPagination } =
    useCapsulesTablePagination(rows, CAPSULES_TABLE_PAGE_SIZE);

  const showError = isError && rows.length === 0;
  const showInitialLoading = isLoading && rows.length === 0;
  const showFetchingHint = isFetching && !showInitialLoading;

  return {
    rows: paginatedRows,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
    canRegisterCapsule,
    isCreating,
    showError,
    showInitialLoading,
    showFetchingHint,
    isRegisterCapsuleModalOpen,
    handleRegisterCapsule,
    handleCloseRegisterCapsuleModal,
    handleContinueToDeploy,
    handleRegisterKeys,
    handleRowClick,
    handleRowMenuAction,
    ConfirmDialogComponent,
    page,
    pageCount,
    onPageChange,
    showPagination,
  };
}
